/**
 * CardHeader — shared §2.11 section-card header anatomy for the dashboard
 * widgets: [icon 18/1.5px --text-low] 10 [h2-role title 17/600 --text-hi]
 * … [right slot: "See all" quiet link OR custom actions]. An optional 13px
 * subtitle sits 4px under the title row.
 *
 * Keeps every chart/panel card speaking the same header language as the KPI
 * StatTiles (which already carry leading icons).
 */
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router';

// ---------------------------------------------------------------------------
// 18px / 1.5px-stroke outline icons (§1.6) for the dashboard card headers.
// ---------------------------------------------------------------------------

const HEADER_ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
};

export function IconDonut() {
  return (
    <svg {...HEADER_ICON_PROPS}>
      <path d="M21 12a9 9 0 1 1-9-9" />
      <path d="M21 12a9 9 0 0 0-9-9v9z" />
    </svg>
  );
}

export function IconPulse() {
  return (
    <svg {...HEADER_ICON_PROPS}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

export function IconBars() {
  return (
    <svg {...HEADER_ICON_PROPS}>
      <line x1="6" y1="20" x2="6" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="18" y1="20" x2="18" y2="14" />
    </svg>
  );
}

export function IconGauge() {
  return (
    <svg {...HEADER_ICON_PROPS}>
      <path d="M12 15l3.5-3.5" />
      <path d="M20.3 18a10 10 0 1 0-16.6 0" />
    </svg>
  );
}

export function IconAlertFlag() {
  return (
    <svg {...HEADER_ICON_PROPS}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

export function IconPeople() {
  return (
    <svg {...HEADER_ICON_PROPS}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

export function IconLedger() {
  return (
    <svg {...HEADER_ICON_PROPS}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

export function IconBriefcase() {
  return (
    <svg {...HEADER_ICON_PROPS}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5.5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2V7" />
      <line x1="3" y1="13" x2="21" y2="13" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// "See all" — the §2.11 card-corner quiet link (12/500 --card-link).
// ---------------------------------------------------------------------------

export function SeeAllLink({ to, label = 'See all' }: { to: string; label?: string }) {
  return (
    <Link
      to={to}
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 12,
        fontWeight: 500,
        color: 'var(--card-link)',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none';
      }}
    >
      {label}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// CardHeader
// ---------------------------------------------------------------------------

export interface CardHeaderProps {
  icon: ReactNode;
  title: string;
  /** Optional 13px --text-low subtitle rendered under the title row. */
  sub?: string;
  /** Optional right-aligned slot ("See all" link, month navigator, …). */
  right?: ReactNode;
  style?: CSSProperties;
}

export function CardHeader({ icon, title, sub, right, style }: CardHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        // Wrap so a wide right slot (CrPerResourceChart's ~188px month
        // navigator) drops below the title at narrow card widths instead of
        // colliding with it (clip floors audit finding 11).
        flexWrap: 'wrap',
        ...style,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span aria-hidden="true" style={{ display: 'flex', color: 'var(--text-low)', flexShrink: 0 }}>
            {icon}
          </span>
          <h3
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 17,
              fontWeight: 600,
              color: 'var(--text-hi)',
              margin: 0,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </h3>
        </div>
        {sub && (
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              color: 'var(--text-low)',
              margin: '4px 0 0 28px',
              lineHeight: 1.3,
            }}
          >
            {sub}
          </p>
        )}
      </div>
      {right}
    </div>
  );
}
